import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  Thumbnail,
  DropZone,
  InlineError,
  InlineStack,
  Button,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import { MultiSelect } from "react-multi-select-component";
import { EditIcon } from "@shopify/polaris-icons";

const AddEditPersona = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [optionsTags, setOptionsTags] = useState([]);
  const [item, setItem] = useState({
    title: "",
    age_range: "",
    lifestyle: "",
    shopping_habits: "",
    attitude: "",
    triggers: "",
    challenges: "",
    how_she_speaks: "",
    image: "",
    country: "",
    tags: [],
  });

  const [errors, setErrors] = useState({
    title: "",
  });

  // useEffect(() => {
  //   console.log("tags=", item.tags);
  // }, [item.tags]);

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/personas/${id}`)
      .then(() => {
        navigate(`/admin/personas`);
      })
      .catch(console.error);
  };

  const validateFields = () => {
    const requiredFields = {
      title: null,
      age_range: null,
      image: null,
      country: null,
    };

    const newErrors = {};
    let hasError = false;
    for (const field in requiredFields) {
      const langs = requiredFields[field];
      if (langs) {
        newErrors[field] = {};
        langs.forEach((lang) => {
          if (!item[field]?.[lang]?.toString().trim()) {
            hasError = true;
            newErrors[field][lang] = "This field is required";
          } else {
            newErrors[field][lang] = "";
          }
        });
      } else {
        if (
          item[field] === "" ||
          item[field] === null ||
          item[field] === undefined
        ) {
          hasError = true;
          newErrors[field] = "This field is required";
        } else {
          newErrors[field] = "";
        }
      }
    }

    if (!item.image) {
      hasError = true;
      setFileError("Image is required");
    } else {
      setFileError("");
    }
    console.log(newErrors);
    setErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));

    return !hasError;
  };

  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const [files, setFiles] = useState();
  const [fileError, setFileError] = useState("");
  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );
  const [imageError, setImageError] = useState("");
  const imageKey = useRef("");

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "persona");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem({
          ...item,
          image:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png",
        });
        setImageError("");
      })
      .catch(() => {});
  };

  useEffect(() => {
    // Always fetch tags
    const fetchTags = axios.get("/person-tags");

    // Fetch persona only if edit mode
    const fetchPersona =
      mode === "edit" ? axios.get(`/personas/${id}`) : Promise.resolve(null);

    setIsSaving(true);

    Promise.all([fetchPersona, fetchTags])
      .then(([personaRes, tagsRes]) => {
        const tags = tagsRes?.data?.data?.data || [];

        // ✅ Always set tag options
        setOptionsTags(
          tags.map((tag) => ({
            label: tag.text,
            value: String(tag.id),
          }))
        );

        // ✅ Only set persona if edit mode
        if (personaRes) {
          const data = personaRes?.data?.data || {};
          setItem({
            title: data?.title || "",
            age_range: data?.age_range || "",
            lifestyle: data?.lifestyle || "",
            shopping_habits: data?.shopping_habits || "",
            attitude: data?.attitude || "",
            triggers: data?.triggers || "",
            challenges: data?.challenges || "",
            how_she_speaks: data?.how_she_speaks || "",
            image: data?.image_url || "",
            country: data?.country || "",
            tags: data?.tags?.length
              ? data.tags.map((item) => ({
                  label: item.text,
                  value: String(item.id),
                }))
              : [],
          });
        }

        setIsSaving(false);
      })
      .catch((err) => {
        console.error(err);
        setIsSaving(false);
      });
  }, [mode, id]);

  // useEffect(() => {
  //   if (mode === "edit") {
  //     axios
  //       .get(`/personas/${id}`)
  //       .then((result) => {
  //         const data = result?.data?.data || {};
  //         setItem({
  //           title: data?.title || "",
  //           age_range: data?.age_range || "",
  //           lifestyle: data?.lifestyle || "",
  //           shopping_habits: data?.shopping_habits || "",
  //           attitude: data?.attitude || "",
  //           triggers: data?.triggers || "",
  //           challenges: data?.challenges || "",
  //           how_she_speaks: data?.how_she_speaks || "",
  //           image: data?.image_url || "",
  //           country: data?.country || "",
  //         });
  //         setIsSaving(false);
  //       })
  //       .catch((err) => console.log(err));
  //   }
  // }, []);
  const [error, setError] = useState("");
  const [optionsCountries, setOptionsCountries] = useState([]);

  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Persona`}
      backAction={{
        onAction: () => navigate(`/admin/personas`),
      }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <FormLayout.Group>
            <TextField
              label="Title"
              value={item.title}
              onChange={(value) => handleChange("title", value)}
              error={errors.title}
            />
            <TextField
              label="Age Range"
              value={item.age_range}
              onChange={(value) => handleChange("age_range", value)}
              error={errors.age_range}
            />
          </FormLayout.Group>

          <Select
            label="Country"
            options={optionsCountries}
            value={item.country}
            onChange={(value) => handleChange("country", value)}
            placeholder="Please select"
            error={errors?.country}
          />
          <InlineStack align="space-between" blockAlign="center">
            <Text>Tags</Text>

            <Button
              variant="plain"
              onClick={() => navigate("/admin/personas/edit/tags")}
              icon={EditIcon}
            >
              Edit Tags
            </Button>
          </InlineStack>
          <div
            style={{
              position: "relative",
              zIndex: 9999,
              // marginBottom: "400px",
            }}
          >
            <MultiSelect
              options={optionsTags}
              onChange={(value) => handleChange("tags", value)}
              value={item.tags}
              placeholder="Please select"
              disableSearch
            />
          </div>

          <TextField
            label="Lifestyle"
            value={item.lifestyle}
            onChange={(value) => handleChange("lifestyle", value)}
            multiline={3}
          />

          <TextField
            label="Shopping Habits"
            value={item.shopping_habits}
            onChange={(value) => handleChange("shopping_habits", value)}
            multiline={3}
          />

          <TextField
            label="Attitude"
            value={item.attitude}
            onChange={(value) => handleChange("attitude", value)}
            multiline={3}
          />

          <TextField
            label="Triggers"
            value={item.triggers}
            onChange={(value) => handleChange("triggers", value)}
            multiline={3}
          />

          <TextField
            label="Challenges"
            value={item.challenges}
            onChange={(value) => handleChange("challenges", value)}
            multiline={3}
          />

          <TextField
            label="How She Speaks"
            value={item.how_she_speaks}
            onChange={(value) => handleChange("how_she_speaks", value)}
            multiline={3}
          />

          <Text as="p" fontWeight="regular">
            Image
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image ? item.image : ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={fileError} />

          <InlineError message={error} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );

  function handleSave() {
    if (!validateFields()) {
      return;
    }
    setIsSaving(true);
    const formData = new FormData();

    formData.append("title", item.title);
    formData.append("age_range", item.age_range);
    formData.append("lifestyle", item.lifestyle);
    formData.append("shopping_habits", item.shopping_habits);
    formData.append("attitude", item.attitude);
    formData.append("triggers", item.triggers);
    formData.append("challenges", item.challenges);
    formData.append("how_she_speaks", item.how_she_speaks);
    formData.append("country", item.country);
    mode === "edit" && formData.append("_method", "PATCH");
    item.tags.length &&
      item.tags.forEach((selected, index) =>
        formData.append(`tags[${index}]`, selected.value)
      );
    imageKey.current && formData.append("image", imageKey.current);

    axios
      .post(mode === "new" ? "/personas" : `/personas/${id}`, formData)
      .then(() => {
        mode === "new" ? navigate("/admin/personas") : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
        if (error.response && error.response.data?.message) {
          console.log("error=", error.response.data?.message);
          const serverErrors = error.response.data.message;
          setError(serverErrors);
        } else {
          console.error(error);
        }
      })
      .finally(() => setIsSaving(false));
  }
};

export default AddEditPersona;
